import type { StitchTypeDef } from './stitch-parts';
import type { Tbc, TbcType } from './tbc-types';
import { docDefaultParam, type CrochetSizingDefaults } from './sizing-defaults';

/** Params key holding the per-stitch "follow the guide tangent" flag.
 *  Stored as 0 | 1 because `Tbc.params` is a `Record<string, number>` — using
 *  the existing generic map keeps this off the pattern package's schema. */
export const FOLLOW_TANGENT_KEY = 'followTangent';

/** The factory value of the follow flag for a stitch type — what applies when
 *  neither the stitch nor the document's sizing defaults store one. ON for the
 *  def stitches, which already followed the guide before the toggle existed;
 *  OFF for cluster/popcorn, which never did (SP-119), so existing diagrams
 *  render unchanged. The settings catalog's toggle field reads this too. */
export function followTangentFactoryDefault(type: TbcType): 0 | 1 {
    return type === 'cluster' || type === 'popcorn' ? 0 : 1;
}

/** Whether this stitch's tangent-following parts (the leg-cap top cap, the
 *  sc-family directional `+` crossbar, and the cluster cap / popcorn oval)
 *  should orient along the guide tangent. ABSENT ON THE STITCH ⇒ the
 *  document's sizing default; absent there ⇒ the type's factory value
 *  (followTangentFactoryDefault), so every pre-existing document keeps
 *  today's geometry and no migration is needed. Per-stitch: an asymmetric
 *  stitch gates all of its legs together, so this key is never
 *  `@legId`-namespaced.
 *
 *  When false, callers force `LegFrame.tipTangent = null` — which is exactly
 *  the pre-existing "not guide-snapped" state, so every consumer already falls
 *  back to `frame.perp` without further changes. */
export function followsTangent(t: Tbc, dd: CrochetSizingDefaults | undefined): boolean {
    const v = t.params?.[FOLLOW_TANGENT_KEY]
        ?? docDefaultParam(dd, t.type, FOLLOW_TANGENT_KEY)
        ?? followTangentFactoryDefault(t.type);
    return v !== 0;
}

/** Whether this stitch type has any part that follows the guide tangent, i.e.
 *  whether the toggle would do anything. Derived from the def rather than a
 *  hand-maintained type list so a new def can't silently miss the toggle. The
 *  real criterion is "the def's geometry reads `frame.tipTangent`":
 *  `capBearing` marks the leg-cap top cap, and `scPlusParams` (stitch-defs.ts)
 *  is the shared reader behind the sc-family crossing-bar parts — it is called
 *  by the `plus` part directly, and by the `cross`, `wave`, and `curl` parts
 *  (the reverse/twisted-sc `×` glyph and its accent). `cross` never appears
 *  without `plus` or vice versa in a single def, so checking either id is
 *  sufficient; `wave`/`curl` never appear without `cross` or `plus`. Keep this
 *  a cheap pure predicate over the def's declared parts — do not render
 *  geometry to probe behavior. See the exhaustive classification test in
 *  test/follow-tangent.test.ts, which enumerates every `STITCH_DEFS` entry
 *  against ground truth and guards against a new def silently slipping past
 *  this derivation. */
export function tangentCapable(def: StitchTypeDef): boolean {
    return def.capBearing === true || def.parts.some(p => p.id === 'plus' || p.id === 'cross');
}

/** Whether the follow toggle would do anything for THIS stitch — the gate for
 *  the inspector rows. Def stitches defer to `tangentCapable(def)` (pass
 *  `stitchDef(t.type)`; taken as a parameter so this module stays free of a
 *  stitch-defs → stitch-parts → follow-tangent import cycle). Cluster/popcorn
 *  have no def: they are eligible per stitch, when their member type draws a
 *  cap / top oval — an sc-member gathers to a bare point with nothing to
 *  orient. */
export function followTangentEligible(t: Tbc, def: StitchTypeDef | undefined): boolean {
    if (t.type === 'cluster' || t.type === 'popcorn') {
        const spec = t.type === 'cluster' ? t.cluster : t.popcorn;
        return spec != null && !spec.memberType.startsWith('single-crochet');
    }
    return def != null && tangentCapable(def);
}
